import React, { useEffect, useState } from 'react';
import { Download, LoaderCircle, X } from 'lucide-react';
import { Colonia } from '../../types/colonia';
import { baixarDataUrl, gerarCarteirinhaDataUrl } from './carteirinhaImage';

const normalizarNome = (valor = '') =>
  valor
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

const formatBoolean = (value?: boolean) => (value ? 'Sim' : 'Não');

export const textoLiberacaoPiscina = (autorizacao?: Colonia['autorizacaoPiscina']) => {
  if (autorizacao === 'nao_autorizado') return 'NÃO ESTÁ LIBERADO PARA PISCINA';
  if (autorizacao === 'somente_boia') return 'Está liberado para piscina (somente com boia)';
  return 'Está liberado para piscina';
};

const InfoRow = ({ label, value }: { label: string; value?: React.ReactNode }) => (
  <div className="registration-info-row">
    <span>{label}</span>
    <strong>{value || '-'}</strong>
  </div>
);

const ModalPhoto = ({ label, src }: { label: string; src?: string }) => (
  <div className="registration-modal-photo">
    {src ? <img src={src} alt={label} /> : <div>{label.slice(0, 2)}</div>}
    <span>{label}</span>
  </div>
);

interface FichaAlunoModalProps {
  inscricao: Colonia;
  onClose: () => void;
}

const FichaAlunoModal: React.FC<FichaAlunoModalProps> = ({ inscricao, onClose }) => {
  const [carteirinha, setCarteirinha] = useState({ frente: '', verso: '' });
  const [gerandoCarteirinha, setGerandoCarteirinha] = useState(true);

  useEffect(() => {
    let active = true;
    setGerandoCarteirinha(true);
    Promise.all([gerarCarteirinhaDataUrl(inscricao, 'frente'), gerarCarteirinhaDataUrl(inscricao, 'verso')])
      .then(([frente, verso]) => {
        if (!active) return;
        setCarteirinha({ frente: frente || '', verso: verso || '' });
      })
      .finally(() => {
        if (active) setGerandoCarteirinha(false);
      });
    return () => {
      active = false;
    };
  }, [inscricao]);

  const slug = normalizarNome(inscricao.nomeCompleto);
  const piscinaLiberado = inscricao.autorizacaoPiscina !== 'nao_autorizado';

  return (
    <div className="registration-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="registration-details-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ficha-aluno-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="registration-details-header">
          <div>
            <h3 id="ficha-aluno-title">Ficha do aluno</h3>
            <p>{inscricao.nomeCompleto}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Fechar">
            <X size={20} />
          </button>
        </div>

        <div className="registration-details-body">
          <div className="registration-photo-strip">
            <ModalPhoto label="Criança" src={inscricao.fotoUrl} />
            <ModalPhoto label="Retirada" src={inscricao.fotoUrlRetirada} />
          </div>

          <section>
            <h4>Criança</h4>
            <InfoRow label="Nascimento" value={inscricao.dataNascimento} />
            <InfoRow label="Idade" value={`${inscricao.idade} anos`} />
            <InfoRow label="Turma" value={inscricao.turma} />
            <InfoRow label="Camisa" value={inscricao.tamanhoCamisa} />
            <InfoRow label="Associado" value={inscricao.associadoUba ? inscricao.codigoAssociado || 'Sim' : 'Não'} />
          </section>

          <section>
            <h4>Contato</h4>
            <InfoRow label="Responsável" value={inscricao.nomeMae} />
            <InfoRow label="WhatsApp" value={inscricao.telefoneMae} />
            <InfoRow label="Pai" value={inscricao.nomePai} />
            <InfoRow label="Tel. pai" value={inscricao.telefonePai} />
          </section>

          <section>
            <h4>Retirada</h4>
            <InfoRow label="Principal" value={inscricao.nomeRetirada} />
            <InfoRow label="Parentesco" value={inscricao.parentescoRetirada} />
            {inscricao.pessoasRetirada?.map((pessoa, index) => (
              <InfoRow key={pessoa.id || index} label={`Autorizado ${index + 1}`} value={`${pessoa.nome} - ${pessoa.parentesco}`} />
            ))}
          </section>

          <section>
            <h4>Endereço</h4>
            <InfoRow label="CEP" value={inscricao.cep} />
            <InfoRow
              label="Rua"
              value={`${inscricao.endereco}, ${inscricao.numero}${inscricao.complemento ? ` - ${inscricao.complemento}` : ''}`}
            />
            <InfoRow label="Bairro" value={inscricao.bairro} />
            <InfoRow label="Cidade/UF" value={`${inscricao.cidade}/${inscricao.uf}`} />
          </section>

          <section>
            <h4>Saúde e piscina</h4>
            <InfoRow label="Sabe nadar" value={formatBoolean(inscricao.sabeNadar)} />
            <InfoRow
              label="Piscina"
              value={
                <span className={piscinaLiberado ? 'ficha-piscina-liberado' : 'ficha-piscina-negado'}>
                  {textoLiberacaoPiscina(inscricao.autorizacaoPiscina)}
                </span>
              }
            />
            <InfoRow label="Alergia" value={inscricao.possuiAlergia ? inscricao.alergias || 'Sim' : 'Não'} />
            <InfoRow label="Doença" value={inscricao.possuiDoenca ? inscricao.doencas || 'Sim' : 'Não'} />
            <InfoRow label="Medic." value={inscricao.tomaMedicamento ? inscricao.medicamentos || 'Sim' : 'Não'} />
          </section>

          <section className="ficha-carteirinha-section">
            <h4>Carteirinha</h4>
            {gerandoCarteirinha ? (
              <p className="admin-floating-hint">
                <LoaderCircle size={14} className="admin-spin" />
                Gerando carteirinha...
              </p>
            ) : (
              <div className="ficha-carteirinha-imagens">
                <figure>
                  <img src={carteirinha.frente} alt={`Carteirinha (frente) de ${inscricao.nomeCompleto}`} />
                  <figcaption>
                    <span>Frente</span>
                    <button
                      type="button"
                      onClick={() => baixarDataUrl(carteirinha.frente, `carteirinha-${slug}-frente.png`)}
                    >
                      <Download size={12} />
                      Baixar
                    </button>
                  </figcaption>
                </figure>
                <figure>
                  <img src={carteirinha.verso} alt={`Carteirinha (verso) de ${inscricao.nomeCompleto}`} />
                  <figcaption>
                    <span>Verso</span>
                    <button
                      type="button"
                      onClick={() => baixarDataUrl(carteirinha.verso, `carteirinha-${slug}-verso.png`)}
                    >
                      <Download size={12} />
                      Baixar
                    </button>
                  </figcaption>
                </figure>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default FichaAlunoModal;
